import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type {
  CustomFieldType,
  ReleaseCustomFieldDefinition,
  ReleaseType,
} from "../../shared/types/api";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import { RELEASE_TYPE_LABELS } from "../releases/releaseVocabulary";

const TYPES: [CustomFieldType, string, string][] = [
  ["TEXT", "Text", "One line — a reference number, a name."],
  ["TEXT_AREA", "Text area", "Several lines — a justification, a note."],
  ["NUMBER", "Number", "Checked as a number, so \"about 40\" is refused rather than stored."],
  ["DATE", "Date", "Checked as a real date."],
  ["BOOLEAN", "Yes / no", "A single tick."],
  ["SELECT", "List", "One of a fixed set of options you supply."],
  ["URL", "Link", "Checked as a full link, so a half-typed one is caught at the form."],
];

const RELEASE_TYPES: ReleaseType[] = [
  "MAJOR", "MINOR", "PATCH", "HOTFIX", "EMERGENCY",
  "UPGRADE", "INFRASTRUCTURE", "CONFIGURATION",
];

/**
 * Defining a question releases have to answer (§58-§61).
 *
 * <p>The key is fixed once created and the label is not. Exports and integrations refer to the
 * key; people read the label and rename it freely — conflating the two means renaming a field on
 * screen breaks whatever was reading it.
 *
 * <p>Marking a field required is a bigger decision than it looks: an unanswered one fails the
 * readiness gate by name, on every release of that kind, from the moment it is saved. The drawer
 * says so rather than letting somebody discover it from a blocked release.
 */
export function ReleaseFieldDrawer({
  field,
  projectId,
  open,
  onClose,
  onSaved,
}: {
  field: ReleaseCustomFieldDefinition | null;
  /** Null means the field applies to every project. */
  projectId: string | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = field != null;
  const [fieldKey, setFieldKey] = useState("");
  const [label, setLabel] = useState("");
  const [helpText, setHelpText] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType>("TEXT");
  const [options, setOptions] = useState("");
  const [required, setRequired] = useState(false);
  const [appliesTo, setAppliesTo] = useState<string>("");
  const [displayOrder, setDisplayOrder] = useState("10");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFieldKey(field?.fieldKey ?? "");
    setLabel(field?.label ?? "");
    setHelpText(field?.helpText ?? "");
    setFieldType(field?.fieldType ?? "TEXT");
    setOptions((field?.options ?? []).join("\n"));
    setRequired(field?.required ?? false);
    setAppliesTo(field?.appliesTo ?? "");
    setDisplayOrder(String(field?.displayOrder ?? 10));
    setError(null);
  }, [open, field]);

  const needsOptions = fieldType === "SELECT";
  const optionsGiven = options.split("\n").map((line) => line.trim()).filter(Boolean);
  const canSubmit =
    label.trim() !== ""
    && (editing || fieldKey.trim() !== "")
    && (!needsOptions || optionsGiven.length > 0);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        label: label.trim(),
        helpText: helpText.trim() === "" ? null : helpText.trim(),
        fieldType,
        options: needsOptions ? optionsGiven.join("\n") : null,
        required,
        appliesTo: appliesTo === "" ? null : appliesTo,
        displayOrder: Number(displayOrder) || 0,
      };
      if (field) {
        await api.put(`/api/v1/release-fields/${field.id}`, {
          ...body,
          lockVersion: field.lockVersion,
        });
      } else {
        await api.post("/api/v1/release-fields", {
          ...body,
          projectId,
          fieldKey: fieldKey.trim(),
        });
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that field.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Edit field" : "New release field"}
      subtitle={
        projectId
          ? "Asked of releases in this project."
          : "Asked of releases in every project."
      }
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : editing ? "Save" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field
          label="Key"
          required={!editing}
          hint={
            editing
              ? "Fixed once created — exports and integrations refer to the field by this."
              : "Lower-cased and underscored automatically. cab_reference, contract_number."
          }
        >
          <Input
            autoFocus={!editing}
            value={fieldKey}
            disabled={editing}
            placeholder="cab_reference"
            onChange={(event) => setFieldKey(event.target.value)}
          />
        </Field>

        <Field label="Label" required hint="What people see on the release.">
          <Input
            value={label}
            placeholder="CAB reference"
            onChange={(event) => setLabel(event.target.value)}
          />
        </Field>

        <Field label="Help text" hint="Shown under the field. Say what a good answer looks like.">
          <Input value={helpText} onChange={(event) => setHelpText(event.target.value)} />
        </Field>

        <Field label="Kind of answer" required>
          <Select
            value={fieldType}
            onChange={(event) => setFieldType(event.target.value as CustomFieldType)}
          >
            {TYPES.map(([value, name]) => (
              <option key={value} value={value}>
                {name}
              </option>
            ))}
          </Select>
        </Field>
        <p className="-mt-2 text-xs text-ink-500">
          {TYPES.find(([value]) => value === fieldType)?.[2]}
        </p>

        {needsOptions && (
          <Field label="Options" required hint="One per line. This is what people choose from.">
            <Textarea
              rows={4}
              value={options}
              placeholder={"Internal\nConfidential\nPublic"}
              onChange={(event) => setOptions(event.target.value)}
            />
          </Field>
        )}

        <Field
          label="Asked of"
          hint="Narrow it to one kind of release — an incident reference only hotfixes need, say."
        >
          <Select value={appliesTo} onChange={(event) => setAppliesTo(event.target.value)}>
            <option value="">Every kind of release</option>
            {RELEASE_TYPES.map((type) => (
              <option key={type} value={type}>
                {RELEASE_TYPE_LABELS[type]}
              </option>
            ))}
          </Select>
        </Field>

        <label className="flex items-start gap-2 text-sm text-ink-800">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-ink-300"
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
          />
          <span>
            A release cannot go out without it
            <span className="block text-xs text-ink-500">
              An unanswered required field fails the readiness gate by name, on every release this
              applies to, from the moment you save.
            </span>
          </span>
        </label>

        {required && !editing && (
          <InlineAlert tone="warning">
            Existing releases that have not answered this will start failing their Required fields
            gate straight away.
          </InlineAlert>
        )}

        <Field label="Order" hint="Where it sits on the release. Lower comes first.">
          <Input
            type="number"
            min={0}
            value={displayOrder}
            onChange={(event) => setDisplayOrder(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
